import { useState } from 'react'
import { Loader2, ShieldAlert } from 'lucide-react'
import { useUserStore } from '@/store/useUserStore'
import { endImpersonation, getImpersonator, isImpersonatingNow } from '@/lib/impersonation'

const BASE_URL = import.meta.env.VITE_API_URL

/**
 * Sticky banner shown across every page of an impersonation tab. Returns
 * null when the tab is not in impersonation mode, so it can be mounted at
 * the app root without conditionals at the call site.
 */
export const ImpersonationBanner = () => {
  const user = useUserStore((s) => s.user)
  const token = useUserStore((s) => s.token)
  const [ending, setEnding] = useState(false)

  if (!isImpersonatingNow()) return null

  const impersonator = getImpersonator()
  if (!impersonator || !user) return null

  const targetName = user.user_profile?.full_name || user.user?.name || '(user)'

  const handleEnd = async () => {
    setEnding(true)
    await endImpersonation(BASE_URL, token)
    // Try to close the tab; fall back to a redirect if the browser blocks
    // window.close() (only allowed on tabs opened by script).
    window.close()
    window.location.replace('/')
  }

  return (
    <div
      data-html2canvas-ignore="true"
      className="shrink-0 z-60 flex flex-col items-center justify-between gap-2 border-b border-amber-300 bg-amber-100 px-4 py-2 text-amber-900 shadow-sm sm:flex-row"
    >
      <div className="flex items-center gap-2 text-sm font-medium">
        <ShieldAlert size={16} className="shrink-0" />
        <span>
          You're impersonating <strong>{targetName}</strong> as{' '}
          <strong>{impersonator.name}</strong>
          <span className="text-amber-700"> ({impersonator.email})</span>
        </span>
      </div>
      <button
        onClick={handleEnd}
        disabled={ending}
        className="inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-amber-600 px-4 py-1 text-xs font-bold text-white transition hover:bg-amber-700 disabled:opacity-60"
      >
        {ending ? (
          <>
            <Loader2 size={12} className="animate-spin" />
            Ending…
          </>
        ) : (
          'End impersonation'
        )}
      </button>
    </div>
  )
}
